import type { Property_Transactions_Distance_Bool_Exp } from '@graphql/__generated__/graphql';

type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const hasOnlyKeys = (
  value: unknown,
  allowedKeys: readonly string[],
): value is PlainObject =>
  isPlainObject(value) &&
  Object.keys(value).every(key => allowedKeys.includes(key));

const supportedComparisonOperators: Record<string, readonly string[]> = {
  property_main_type: ['_in'],
  signature_date: ['_gte', '_lte'],
  living_surface: ['_gte', '_lte'],
  land_surface: ['_gte', '_lte'],
  number_of_rooms: ['_gte', '_lte'],
  purchase_price: ['_gte', '_lte'],
  postcode: ['_eq'],
};

export const hasUnsupportedExternalTransactionLocalities = (
  localityPlaceIds: readonly string[],
) => localityPlaceIds.length > 1;

export const hasUnsupportedExternalTransactionFilters = (
  where: Property_Transactions_Distance_Bool_Exp,
) => {
  if (!hasOnlyKeys(where, ['transaction'])) {
    return true;
  }

  const transaction = where.transaction;
  if (transaction == null) {
    return false;
  }
  if (!isPlainObject(transaction)) {
    return true;
  }

  return Object.entries(transaction).some(([field, comparison]) => {
    if (field === 'locality_place') {
      if (!hasOnlyKeys(comparison, ['id'])) {
        return true;
      }
      return !hasOnlyKeys((comparison as PlainObject).id, ['_eq', '_in']);
    }

    const operators = supportedComparisonOperators[field];
    return operators == null || !hasOnlyKeys(comparison, operators);
  });
};
